// src/pages/UpcomingProjectsPage.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ProjectsSection from '../components/ProjectsSection';
import '../styles/ProjectsPage.css';

const UpcomingProjectsPage = () => {
  const [upcomingProjects, setUpcomingProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

  useEffect(() => {
    const fetchUpcomingProjects = async () => {
      try {
        setLoading(true);
        console.log('Fetching upcoming projects...');
        
        const response = await fetch(`${API_BASE_URL}/get_projects.php`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('API Response:', result);
        
        if (result.success && result.projects) {
          // Filter only upcoming projects
          const upcoming = result.projects.filter(
            project => project.project_type === 'Upcoming Projects'
          );
          
          // Map the data to match your ProjectsSection component format
          const mappedProjects = upcoming.map((project, index) => {
            // Parse key features if it's a string
            const features = project.key_features 
              ? project.key_features.split(',').map(f => f.trim()).filter(f => f)
              : ['Innovative Design', 'Premium Quality', 'Strategic Location'];
            
            // Get images array and prepare them with full URLs
            const images = Array.isArray(project.images) && project.images.length > 0
              ? project.images.map(img => `${BASE_URL}${img}`)
              : [];
            
            return {
              id: project.id,
              title: project.client_name,
              location: project.location,
              type: 'Residential', // You can add this field to your database if needed
              area: `${project.area} sqft`,
              units: project.floors || 'Multiple Units', // Can be adjusted based on your needs
              floors: project.floors,
              launchDate: new Date(project.start_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long'
              }),
              expectedStart: new Date(project.start_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long'
              }),
              estimatedBudget: '₹TBD', // Add this field to database if needed
              image: images[0] || '', // First image as main image
              images: images, // All images for slider
              features: features,
              description: project.project_title || 'An exciting upcoming project with modern amenities.',
              color: index % 2 === 0 ? 'blue' : 'red', // Alternate colors
              progress: project.progress || 0,
              phase: project.phase,
              startDate: project.start_date,
              completionDate: project.completion_date
            };
          });
          
          console.log('Mapped upcoming projects:', mappedProjects);
          setUpcomingProjects(mappedProjects);
          setError(null);
        } else {
          throw new Error(result.error || 'Failed to fetch projects');
        }
      } catch (err) {
        console.error('Error fetching upcoming projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingProjects();
  }, [API_BASE_URL, BASE_URL]);

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
      }}>
        <div className="loading-spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading upcoming projects...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px',
        padding: '20px'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#e74c3c'
        }}>⚠</div>
        <h2 style={{ color: '#e74c3c', margin: '0' }}>Error Loading Projects</h2>
        <p style={{ color: '#666', textAlign: 'center' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (upcomingProjects.length === 0) {
    return (
      <div className="no-projects-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
      }}>
        <div style={{ fontSize: '64px' }}>🔮</div>
        <h2 style={{ color: '#666', margin: '0' }}>No Upcoming Projects</h2>
        <p style={{ color: '#999' }}>There are no upcoming projects to display at the moment.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="projects-page"
    >
      {/* Projects Section */}
      <ProjectsSection projects={upcomingProjects} type="upcoming" />
    </motion.div>
  );
};

export default UpcomingProjectsPage;